import { FC } from 'react';
import classnames from 'classnames';
import { BiX } from 'react-icons/bi';
import type { ButtonType } from 'antd/lib/button';
import type { ModalProps } from 'antd/lib/modal';
import { IconButton, Button as MUIButton } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { Close, CheckOutlined, SaveOutlined } from '@mui/icons-material';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

// internal
import Button from '@app/components/button';
import { CustomModal } from './styledComponent';

interface IProps extends ModalProps {
  className?: string;
  isShow: boolean;
  closable?: boolean;
  editWidth?: number;
  size?: 1 | 2;
  title: string;
  subTitle?: string;
  loadingProgress?: number;
  loadingButton?: () => void;
  loadingButtonLabel?: string;
  disablePrimaryMuiButton?: boolean;
  primaryHide?: boolean;
  primaryLabel?: string;
  primaryMuiLabel?: string;
  primaryType?: string;
  primaryColor?: ButtonType;
  primaryOnClick?: () => void;
  primaryMui?: () => void;
  processModal?: boolean;
  secondaryHide?: boolean;
  secondaryLabel?: string;
  secondaryMuiLabel?: string;
  secondaryType?: string;
  secondaryColor?: ButtonType;
  secondaryMui?: () => void;
  secondaryOnClick?: () => void;
  saveMui?: () => void;
  saveMuiLabel?: string;
  iconClose?: () => void;
  closeMui?: () => void;
  closeIconMui?: () => void;
  onCancel?: () => void;
  formId?: string;
  activateBar?: boolean;
}

const Modal: FC<IProps> = (props) => {
  return (
    <CustomModal
      centered={true}
      closable={props.closable || false}
      footer={null}
      onCancel={props.secondaryOnClick || props.onCancel}
      fontSize={props.size}
      width={props.editWidth}
      {...props}
      visible={props.isShow}
      title=""
    >
      <div className="title flex justify-between">
        <span className="text-gray-600 text-xl">{props.title}</span>
        {props.iconClose && (
          <Button htmlType="button" onClick={props.iconClose}>
            <BiX className="text-2xl margin-0" />
          </Button>
        )}
        {props.closeMui && (
          <MUIButton
            variant="contained"
            sx={{ minWidth: 2, height: 40 }}
            onClick={props.closeMui}
            size="small"
          >
            <Close />
          </MUIButton>
        )}
        {props.closeIconMui && (
          <IconButton onClick={props.closeIconMui}>
            <Close />
          </IconButton>
        )}
      </div>

      {props.activateBar && <div className="items-center border-b" />}
      <div className="description">{props.children}</div>
      <div
        className={classnames('action flex justify-center', {
          hidden: props.primaryHide && props.secondaryHide,
        })}
      >
        <div className="start">
          {!props.primaryHide && (
            <Button
              form={props.formId}
              htmlType={props.primaryType as any}
              hideShadow={true}
              onClick={props.primaryOnClick}
            >
              {props.primaryLabel}
            </Button>
          )}
        </div>
        <div>
          {props.primaryMui && (
            <LoadingButton
              variant="contained"
              sx={{ width: 150, marginTop: 5, marginRight: 5 }}
              loading={props.processModal}
              loadingPosition="start"
              onClick={props.primaryMui}
              size="large"
              startIcon={<CheckOutlined />}
              disabled={props.disablePrimaryMuiButton}
            >
              {props.primaryMuiLabel}
            </LoadingButton>
          )}
        </div>
        <div>
          {!props.secondaryHide && (
            <Button
              type={props.secondaryColor}
              htmlType={props.secondaryType as any}
              onClick={props.secondaryOnClick}
            >
              {props.secondaryLabel}
            </Button>
          )}
        </div>
        <div>
          {props.secondaryMui && (
            <LoadingButton
              variant="contained"
              startIcon={<Close />}
              loading={props.processModal}
              loadingPosition="start"
              sx={{ width: 150, marginTop: 5 }}
              color="secondary"
              onClick={props.secondaryMui}
              size="large"
            >
              {props.secondaryMuiLabel}
            </LoadingButton>
          )}
        </div>
        <div>
          <div className="action justify-center">
            {props.loadingProgress !== 0 && props.loadingButton && (
              <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                <CircularProgress variant="determinate" value={props.loadingProgress} size={80} />
                <Box
                  sx={{
                    top: 0,
                    left: 0,
                    bottom: 0,
                    right: 0,
                    position: 'absolute',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography
                    variant="caption"
                    component="div"
                    color="text.secondary"
                  >{`${Math.round(props.loadingProgress || 0)}%`}</Typography>
                </Box>
              </Box>
            )}
          </div>
          <div>
            {props.loadingButton && (
              <LoadingButton
                variant="contained"
                startIcon={<CheckOutlined />}
                loading={props.processModal}
                loadingPosition="start"
                sx={{ width: 150, marginTop: 8, marginBottom: 3 }}
                onClick={props.loadingButton}
                size="large"
              >
                {props.loadingButtonLabel}
              </LoadingButton>
            )}
          </div>
        </div>
      </div>
      <div className="flex justify-end">
        {props.saveMui && (
          <LoadingButton
            variant="contained"
            startIcon={<SaveOutlined />}
            loading={props.processModal}
            loadingPosition="start"
            sx={{ width: 150, marginTop: 5 }}
            onClick={props.saveMui}
            size="large"
          >
            {props.saveMuiLabel}
          </LoadingButton>
        )}
      </div>
    </CustomModal>
  );
};

Modal.defaultProps = {
  primaryLabel: 'OK',
  primaryHide: false,
  primaryType: 'button',
  secondaryLabel: 'Cancel',
  secondaryHide: false,
  secondaryType: 'button',
};

export default Modal;
